/**
 * ONE provider-facing ETA — the live, SERVER-stamped one.
 *
 * 2026-10-20 field report: the provider's journey card said "~10 min" while the
 * always-on "active session" pill (and, in the shade, the ongoing tracking
 * notification) said ~45. Root cause: the pill rendered the ELAPSED minutes
 * since travel started ("On the way · 46 min" — the trip had taken 46 min when
 * the live ETA read 10), indistinguishable from a countdown. The card, in turn,
 * recomputed its own ETA client-side while the member and the server used the
 * ETA stamped on each accepted ping — three derivations of "how far away".
 *
 * Every provider-facing surface (journey card chip, active-session pill, the
 * Android ongoing notification — also updated natively from the ping
 * response) now reads the newest server-stamped `etaMinutes` on the journey's
 * pings through {@link liveProviderEtaMinutes}, and renders it through
 * {@link formatProviderEtaAway}. The native layer mirrors both
 * (`NativeLivePinger.parseLiveEtaMinutes`, `BackgroundLocationService.etaTitle`)
 * — keep the max age and the wording below in sync with those.
 */

import type { SessionJourney } from './types'

/**
 * A server ETA older than this is no longer shown ("stale beats nothing" is
 * wrong for a countdown): surfaces drop the number rather than display a
 * minutes-old figure. Pings flow every ≤ 5 min even when stationary, so a live
 * journey normally stays well inside it. Mirror: NativeLivePinger.MAX_ETA_AGE_MS.
 */
export const PROVIDER_ETA_MAX_AGE_MS = 10 * 60_000

/**
 * Minutes until arrival from the newest accepted ping carrying a server ETA
 * (≥ 1), or `null` when the journey is not en route, has no ETA, or the ETA is
 * older than {@link PROVIDER_ETA_MAX_AGE_MS}.
 */
export function liveProviderEtaMinutes(
  journey: SessionJourney | null | undefined,
  now: number = Date.now(),
): number | null {
  if (!journey || journey.status !== 'en_route') return null
  for (let i = journey.pings.length - 1; i >= 0; i--) {
    const ping = journey.pings[i]
    const eta = ping.etaMinutes
    if (typeof eta !== 'number' || !Number.isFinite(eta) || eta < 0) continue
    // The newest ETA-carrying ping decides — never skip back to an older one.
    if (now - ping.timestamp > PROVIDER_ETA_MAX_AGE_MS) return null
    return Math.max(1, Math.round(eta))
  }
  return null
}

/** "~10 min away" — the one wording for a live provider ETA. */
export function formatProviderEtaAway(minutes: number): string {
  return `~${Math.max(1, Math.round(minutes))} min away`
}
